import { z } from 'zod';
import {
  IMPORT_ISSUE_CODES,
  customerImportFieldSchema,
  importTargetSchema,
} from '../import/customer-import.js';
import { MAX_IMPORT_ROWS } from '../import/spreadsheet.js';

/**
 * The import contract: rows in, a verdict on every one of them out.
 *
 * The browser parses the file (a CSV parser and an XLSX reader that both run in either
 * runtime), so what crosses the wire is a grid of strings and a mapping — never the file
 * itself. That keeps binary uploads out of an API that has no business handling them, and it
 * means the preview screen and the import send the SAME payload: the preview is the import,
 * with the writing turned off.
 *
 * Which is the point. `POST /preview` and `POST /` take one shape, and the API validates it
 * from scratch both times. A client that skipped the preview cannot skip the validation.
 */

export const importCellSchema = z.string().max(500);

export const importRowSchema = z.object({
  /** The line number in the owner's own file, so every message names a row they can open. */
  rowNumber: z.number().int().min(1),
  cells: z.array(importCellSchema).max(200),
});
export type ImportRowInput = z.infer<typeof importRowSchema>;

export const customerImportRequestSchema = z.object({
  /** One target per column, in file order. Columns past the end of the list are ignored. */
  mapping: z.array(importTargetSchema).max(200),
  rows: z.array(importRowSchema).max(MAX_IMPORT_ROWS),
  /**
   * The garment template any measurement columns belong to. A spreadsheet column headed
   * "Bust" is a blouse measurement or a kurti one and the file cannot say which, so the owner
   * picks it once for the whole import rather than per column.
   */
  measurementGarmentTypeKey: z.string().min(1).max(60).nullish(),
});
export type CustomerImportRequest = z.infer<typeof customerImportRequestSchema>;

/**
 * What happened to one row.
 *
 * `ready` is the preview's "this would be created"; `created` is the import's "it was".
 * `skipped` is a row that is perfectly valid and already in the book — a duplicate, which is
 * an outcome rather than a failure and must not be counted as one.
 */
export const IMPORT_ROW_STATUSES = ['ready', 'created', 'skipped', 'failed'] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

export const importIssueSchema = z.object({
  field: customerImportFieldSchema.nullable(),
  code: z.enum(IMPORT_ISSUE_CODES),
  severity: z.enum(['error', 'warning']),
  message: z.string(),
});

export const importRowResultSchema = z.object({
  rowNumber: z.number().int(),
  status: z.enum(IMPORT_ROW_STATUSES),
  /** What the row resolved to, for the preview table. Null when it could not be read. */
  name: z.string().nullable(),
  phone: z.string().nullable(),
  /** How many measurement values this row carries — the preview says so before writing. */
  measurementCount: z.number().int().nonnegative(),
  issues: z.array(importIssueSchema),
  /** The row's original cells, so a rejected row can be written back out verbatim. */
  cells: z.array(z.string()),
});
export type ImportRowResult = z.infer<typeof importRowResultSchema>;

export const importSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  /** Rows that would be — or were — written. */
  ready: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** Measurement profiles created alongside the customers. */
  profilesCreated: z.number().int().nonnegative(),
});
export type ImportSummary = z.infer<typeof importSummarySchema>;

export const customerImportResultSchema = z.object({
  summary: importSummarySchema,
  rows: z.array(importRowResultSchema),
});
export type CustomerImportResult = z.infer<typeof customerImportResultSchema>;
